import { Dayjs } from "dayjs";
import { useEffect } from "react";
import {
  EventLabellingModelStatus,
  EventsForReview,
  useGetEventsForReviewQuery,
  useGetLabellerTimelinesReportQuery,
} from "src/store/api/generatedApi";
import { TimelineExclusionPeriodIds } from "../utils/timelineExclusions";
import { reviewEventsQueryArgs } from "../utils/reviewEventsQueryArgs";
import {
  TimelineExclusionReport,
  TimelineExclusions,
  useTimelineExclusions,
} from "./useTimelineExclusions";
import { useSupersededReviewEventsResponse } from "./useSupersededReviewEventsResponse";

const NO_EXCLUSIONS: ReadonlySet<number> = new Set();

interface ReviewEventsWithExclusions {
  /**
   * The response for the exclusions in force, once they are resolved. A surface that must
   * not present a merge built from every labeller renders on this and nothing else.
   */
  resolvedResponse: EventsForReview | undefined;
  /**
   * The last response received for this window's own arguments, which lags a re-key. For a
   * surface that would rather keep showing the previous merge than fall back to a spinner.
   */
  latestResponse: EventsForReview | undefined;
  /**
   * The merge this window last resolved, kept while a re-merge is in flight so a surface can
   * leave it on screen instead of emptying itself. Undefined until the first one lands, and
   * dropped when the window changes — a new window's spinner must not sit over the previous
   * window's events.
   */
  supersededResponse: EventsForReview | undefined;
  exclusions: TimelineExclusions;
  isError: boolean;
  isFetching: boolean;
  refetch: () => void;
}

/**
 * A review-events fetch whose merge excludes the labellers the surface has decided to leave
 * out.
 *
 * Deciding them takes two passes, and cannot take fewer: the labelling periods to exclude are
 * only knowable from a response, and the fetch is keyed on them. So a probe fetch reports what
 * the window contains, `resolveExclusions` turns that into period ids, and applying them
 * re-keys the fetch that is rendered.
 *
 * They are resolved from `currentData`, the response for the arguments in force. `data` lags a
 * re-key, so resolving from it would write the previous window's period ids into this
 * window's sets.
 */
export const useReviewEventsWithExclusions = ({
  tenantName,
  location,
  windowStart,
  windowEnd,
  resolveExclusions,
  inheritedExclusions,
  probePaddingSeconds = 0,
  includeVerified,
  omittedModelStatuses,
  skip = false,
}: {
  tenantName: string;
  location: string;
  windowStart: Dayjs;
  windowEnd: Dayjs;
  /**
   * Turns a window's reported labellers into the labelling periods to keep out of its merge.
   * Must be stable across renders. The review page resolves its own defaults from what the
   * response reports; a surface that has to agree with the page inherits the page's decisions
   * instead of re-deriving them.
   */
  resolveExclusions: (
    report: TimelineExclusionReport
  ) => TimelineExclusionPeriodIds;
  inheritedExclusions?: TimelineExclusionPeriodIds;
  /**
   * Seconds either side of the window whose labelling periods also feed its merge. The
   * backend pads the merge by `reservation.chunk_seconds` but reports labellers only over
   * the window itself, so the chunk ending on the window's start is invisible while its
   * events are merged — and a surface whose event ids must line up with another's has to
   * resolve it. Pass the padding to widen the probe over the whole contributing range.
   *
   * Zero keeps the probe on the window itself, where it doubles as the rendered response
   * whenever nothing needs excluding, and so costs no request of its own.
   */
  probePaddingSeconds?: number;
  includeVerified?: boolean;
  omittedModelStatuses?: readonly EventLabellingModelStatus[];
  skip?: boolean;
}): ReviewEventsWithExclusions => {
  const windowKey = `${windowStart.toISOString()}|${windowEnd.toISOString()}`;
  const exclusions = useTimelineExclusions({
    windowKey,
    inherited: inheritedExclusions,
  });
  const { applyResolved, resolved } = exclusions;

  // A widened probe answers different arguments from the render, so issuing the render
  // fetch before the probe reports would spend a request on a merge nothing will show.
  // An unwidened probe shares the render's arguments until a default lands, so it must not
  // be skipped or the two would stop being one request.
  const probeIsSeparateRequest = probePaddingSeconds > 0;

  // A widened probe has no merge to share, so it asks the endpoint that reports labellers
  // and nothing else.
  const widenedProbeQuery = useGetLabellerTimelinesReportQuery(
    {
      tenantName,
      location,
      windowStart: windowStart
        .subtract(probePaddingSeconds, "second")
        .toISOString(),
      windowEnd: windowEnd.add(probePaddingSeconds, "second").toISOString(),
    },
    { skip: skip || !probeIsSeparateRequest }
  );
  const sharedProbeQuery = useGetEventsForReviewQuery(
    reviewEventsQueryArgs({
      tenantName,
      location,
      windowStart,
      windowEnd,
      excludedPeriodIds: NO_EXCLUSIONS,
      excludeMaterialPeriodIds: NO_EXCLUSIONS,
      excludeTimingPeriodIds: NO_EXCLUSIONS,
      includeVerified,
      omittedModelStatuses,
    }),
    { skip: skip || probeIsSeparateRequest }
  );
  const probeQuery = probeIsSeparateRequest
    ? widenedProbeQuery
    : sharedProbeQuery;
  const probeResponse: TimelineExclusionReport | undefined =
    probeQuery.currentData;

  useEffect(() => {
    applyResolved(probeResponse && resolveExclusions(probeResponse));
  }, [probeResponse, resolveExclusions, applyResolved]);

  const isEventsQuerySkipped = skip || (probeIsSeparateRequest && !resolved);
  const query = useGetEventsForReviewQuery(
    reviewEventsQueryArgs({
      tenantName,
      location,
      windowStart,
      windowEnd,
      excludedPeriodIds: exclusions.excludedPeriodIds,
      excludeMaterialPeriodIds: exclusions.materialExcludedPeriodIds,
      excludeTimingPeriodIds: exclusions.timingExcludedPeriodIds,
      includeVerified,
      omittedModelStatuses,
    }),
    { skip: isEventsQuerySkipped }
  );

  const resolvedResponse = resolved ? query.currentData : undefined;
  const supersededResponse = useSupersededReviewEventsResponse({
    windowKey,
    resolvedResponse,
  });

  return {
    resolvedResponse,
    supersededResponse,
    latestResponse: query.data,
    exclusions,
    isError: probeQuery.isError || query.isError,
    isFetching: probeQuery.isFetching || query.isFetching,
    // RTK Query throws when asked to refetch a skipped query.
    refetch: () => {
      if (skip) return;
      void probeQuery.refetch();
      if (!isEventsQuerySkipped) void query.refetch();
    },
  };
};
